/**
 * Centralized API Service for Coderom Dropshipping Platform
 * Handles all API calls with authentication, refresh tokens, and error handling
 */
import { parseApiErrorBody, buildApiError } from "../utils/apiError";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Track if we're already redirecting to login (prevent loops)
let isRedirectingToLogin = false;

// Helper to get auth headers
const getAuthHeaders = (): HeadersInit => {
  const headers: HeadersInit = {};

  const authUser = localStorage.getItem("authUser");
  if (authUser) {
    try {
      const parsed = JSON.parse(authUser);
      const token = parsed?.access_token || parsed?.token;
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    } catch (e) {
      console.error("Error parsing authUser:", e);
    }
  }

  return headers;
};

// [K1-UNLOAD] Sayfa kapanirken/yenilenirken bekleyen taslak kuyrugunu gonderir.
// Eskiden navigator.sendBeacon('/api/drafts/save-bulk') kullaniliyordu: goreli adres
// app.coderom.com'a gidiyordu (o vhost'ta /api yok -> 405) ve beacon Authorization
// basligi tasiyamiyordu (backend yalniz Bearer kabul eder) -> kuyruk sessizce kayboluyordu.
// keepalive fetch sayfa kapandiktan sonra da tamamlanir ve baslik tasir.
// Tarayici siniri: keepalive govdesi toplam ~64 KB; asilirsa istek reddedilir.
export const saveDraftsBulkOnUnload = (drafts: Array<{ asin: string; data: any }>): boolean => {
  if (!drafts || drafts.length === 0) return false;
  try {
    const p = fetch(`${API_BASE}/api/drafts/save-bulk`, {
      method: "POST",
      keepalive: true,
      headers: { ...(getAuthHeaders() as Record<string, string>), "Content-Type": "application/json" },
      body: JSON.stringify({ drafts }),
    });
    if (p && typeof (p as any).catch === "function") {
      (p as Promise<Response>).catch(() => undefined);
    }
    return true;
  } catch (e) {
    return false;
  }
};

// Access token currently in storage (null if none) — used to detect a late 401
export const getStoredAccessToken = (): string | null => {
  try {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const parsed = JSON.parse(authUser);
      return parsed?.access_token || parsed?.token || null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};

// Helper to get current user info
export const getCurrentUser = () => {
  const authUser = localStorage.getItem("authUser");
  if (authUser) {
    try {
      const parsed = JSON.parse(authUser);
      return parsed?.user || null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Check if we have a valid token
const hasValidToken = (): boolean => {
  try {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const parsed = JSON.parse(authUser);
      return !!(parsed?.access_token || parsed?.token);
    }
  } catch (e) {
    // ignore
  }
  return false;
};

// ============ MULTI-STORE SUPPORT ============
// Get current selected store ID from localStorage (set by StoreContext)
const getSelectedStoreId = (): number | null => {
  try {
    // StoreContext saves to 'selectedStoreData' and 'selectedStoreId'
    const storeId = localStorage.getItem("selectedStoreId");
    if (storeId) {
      return parseInt(storeId, 10);
    }
    // Fallback to full store data
    const stored = localStorage.getItem("selectedStoreData");
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.id || null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};

// Endpoints that should NOT have store_id automatically added
const STORE_ID_EXCLUDED_ENDPOINTS = [
  "/api/auth/",
  "/api/ebay/auth",
  "/api/ebay/callback",
  "/api/user-settings",  // Legacy - user-wide settings
  "/api/products/limits", // User-wide limits, not per-store
  "/api/admin/",         // Admin endpoints are user-wide
  "/api/billing/",       // Billing is user-wide
  "/api/profile/",       // Profile is user-wide
];

// Check if endpoint should have store_id injected
const shouldInjectStoreId = (endpoint: string): boolean => {
  // Don't inject if endpoint is excluded
  for (const excluded of STORE_ID_EXCLUDED_ENDPOINTS) {
    if (endpoint.includes(excluded)) {
      return false;
    }
  }
  // Don't inject if already has store_id in URL
  if (endpoint.includes("store_id=")) {
    return false;
  }
  return true;
};

// Inject store_id into URL if needed
const injectStoreId = (endpoint: string): string => {
  if (!shouldInjectStoreId(endpoint)) {
    return endpoint;
  }

  const storeId = getSelectedStoreId();
  if (!storeId) {
    return endpoint;
  }

  // Add store_id to URL
  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}store_id=${storeId}`;
};
// ============ END MULTI-STORE SUPPORT ============

// Get refresh token from storage
const getRefreshToken = (): string | null => {
  try {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const parsed = JSON.parse(authUser);
      return parsed?.refresh_token || null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};

// Fixed logout reason codes, sent as /login?r=<code> so nginx shows why the client logged out.
// No personal data or token; the login page does not read the parameter.
export type LogoutReason =
  | "storage_logout"
  | "refresh_401"
  | "retry_401"
  | "user_logout"
  | "impersonation_exit"
  | "no_refresh_token"
  | "refresh_429"
  | "refresh_5xx"
  | "refresh_network";

export const loginRedirectUrl = (reason: LogoutReason): string => `/login?r=${reason}`;

// Expiry fields for a newly stored access token. Every path that writes tokens without
// updateStoredTokens uses this, so token_expires_at always matches the stored token
// (the F1 SSE decision and the proactive refresh both read it).
export const tokenExpiryFields = (expiresIn?: number) =>
  expiresIn ? { expires_in: expiresIn, token_expires_at: Date.now() + expiresIn * 1000 } : {};

// Non-auth 401: backend products.py answers 401 when the user's eBay connection is gone
// ("eBay token expired", "eBay access token expired. Please reconnect your eBay account.").
// None of our auth 401 messages start with "eBay" (pinned by api.refresh.test.ts).
export const isEbay401 = (body: any): boolean => {
  const message = body?.message ?? body?.detail;
  return typeof message === "string" && message.startsWith("eBay");
};

// Update stored tokens
const updateStoredTokens =(accessToken: string, refreshToken: string, expiresIn?: number) => {
  try {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const parsed = JSON.parse(authUser);
      parsed.access_token = accessToken;
      parsed.token = accessToken;  // Sync token field with access_token
      parsed.refresh_token = refreshToken;
      if (expiresIn) {
        parsed.expires_in = expiresIn;
        parsed.token_expires_at = Date.now() + (expiresIn * 1000);
      }
      localStorage.setItem("authUser", JSON.stringify(parsed));
    }
  } catch (e) {
    console.error("Error updating tokens:", e);
  }
};

// Why the last refresh returned null — becomes the /login?r= code when that failure logs the user out
let lastRefreshFailure: LogoutReason = "refresh_401";
export const getLastRefreshFailure = (): LogoutReason => lastRefreshFailure;

// F2b (i): only a refresh token the server rejects (401 / other 4xx) or a missing one ends the session.
// 429, 5xx, a 200 without tokens, network errors and the timeout are transient: the session is kept and
// the next request that needs a token tries again.
export const isTransientRefreshFailure = (reason: LogoutReason): boolean =>
  reason === "refresh_429" || reason === "refresh_5xx" || reason === "refresh_network";

export const REFRESH_TIMEOUT_MS = 10 * 1000; // a hanging refresh counts as a network error
export const REFRESH_RETRY_DELAYS_MS = [2000, 5000, 15000]; // 5xx / network: up to 3 retries
export const REFRESH_429_DEFAULT_WAIT_MS = 60 * 1000; // 429 without a readable Retry-After (backend 10/minute)
const REFRESH_429_MAX_WAIT_MS = 2 * 60 * 1000;

// Retry-After as seconds or an HTTP date; null when absent or unreadable (cross-origin hides it unless exposed)
export const retryAfterMs = (value: string | null | undefined, now: number = Date.now()): number | null => {
  if (!value) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.min(seconds * 1000, REFRESH_429_MAX_WAIT_MS);
  const date = Date.parse(value);
  if (!Number.isNaN(date)) return Math.min(Math.max(date - now, 0), REFRESH_429_MAX_WAIT_MS);
  return null;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type RefreshAttempt = { token: string | null; reason: LogoutReason; waitMs?: number };

// One refresh request with a timeout; never throws
const refreshOnce = async (refreshToken: string): Promise<RefreshAttempt> => {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS) : null;
  try {
    console.log("[Auth] Attempting to refresh access token...");
    const response = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
      ...(controller ? { signal: controller.signal } : {}),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      console.error(`[Auth] Refresh failed with status ${response.status}:`, errorText);
      if (response.status === 429) {
        const wait = retryAfterMs(response.headers?.get?.("Retry-After"));
        return { token: null, reason: "refresh_429", waitMs: wait ?? REFRESH_429_DEFAULT_WAIT_MS };
      }
      return { token: null, reason: response.status >= 500 ? "refresh_5xx" : "refresh_401" };
    }

    const data = await response.json();
    if (data.access_token && data.refresh_token) {
      console.log("[Auth] Token refresh successful");
      updateStoredTokens(data.access_token, data.refresh_token, data.expires_in);
      return { token: data.access_token, reason: lastRefreshFailure };
    }
    console.warn("[Auth] Refresh response missing tokens:", data);
    return { token: null, reason: "refresh_5xx" };
  } catch (e) {
    console.error("[Auth] Token refresh exception:", e);
    return { token: null, reason: "refresh_network" };
  } finally {
    if (timer) clearTimeout(timer);
  }
};

// Refresh the access token (callers go through refreshAccessToken below).
// 429 -> wait Retry-After (else 60 s), one retry. 5xx / network / timeout -> retries after 2, 5, 15 s.
// When the retries run out the result is null with a transient reason: callers keep the session.
const doRefreshAccessToken = async (): Promise<string | null> => {
  let refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.warn("[Auth] No refresh token found in storage");
    lastRefreshFailure = "no_refresh_token";
    return null;
  }

  let retries5xx = 0;
  let retried429 = false;
  for (;;) {
    const attempt = await refreshOnce(refreshToken);
    if (attempt.token) return attempt.token;
    lastRefreshFailure = attempt.reason;
    if (!isTransientRefreshFailure(attempt.reason)) return null;

    let wait: number | null = null;
    if (attempt.reason === "refresh_429") {
      if (!retried429) {
        retried429 = true;
        wait = attempt.waitMs ?? REFRESH_429_DEFAULT_WAIT_MS;
      }
    } else if (retries5xx < REFRESH_RETRY_DELAYS_MS.length) {
      wait = REFRESH_RETRY_DELAYS_MS[retries5xx++];
    }
    if (wait === null) {
      console.warn(`[Auth] Refresh transient failure, session kept: ${attempt.reason} (retries exhausted)`);
      return null;
    }
    console.warn(`[Auth] Refresh transient failure, session kept: ${attempt.reason}, retry in ${Math.round(wait / 1000)}s`);
    await sleep(wait);

    // Another tab may have refreshed (rotating the refresh token) or logged out while we waited
    const current = getRefreshToken();
    if (!current) {
      lastRefreshFailure = "no_refresh_token";
      return null;
    }
    if (current !== refreshToken) {
      const fresh = getStoredAccessToken();
      if (fresh) return fresh; // the other tab's refresh succeeded: use its token, no request
    }
    refreshToken = current;
  }
};

// Same-tab single flight: while a refresh is running, every caller (apiFetch, SSE, proactive check,
// FloatingChat, Chat) awaits the same request instead of starting a new one. Cleared when it settles,
// so a failure is not cached and a call after completion starts a new request. Cross-tab locking is F2b (ii).
let refreshInFlight: Promise<string | null> | null = null;
export const refreshAccessToken = (): Promise<string | null> => {
  if (!refreshInFlight) {
    refreshInFlight = doRefreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Handle logout and redirect
const handleLogout = (reason: LogoutReason) => {
  console.warn(`[Auth] Logging out user. Reason: ${reason}`);
  localStorage.removeItem("authUser");
  if (!isRedirectingToLogin && !window.location.pathname.includes("/login")) {
    isRedirectingToLogin = true;
    window.location.href = loginRedirectUrl(reason);
  }
};

// Multi-tab token sync — başka tab token güncelleyince veya silince haberdar ol
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key !== 'authUser') return;

    if (!e.newValue) {
      // Başka tab logout yaptı — bu tab'ı da logout et
      handleLogout("storage_logout");
      return;
    }
    // Token written by another tab: requests in this tab read storage on every send, and a late 401
    // resends with the new token (apiFetch), so nothing to release here.
  });
}

// Generic fetch wrapper with error handling and token refresh.
// attempt = sends so far (0, 1, 2): a request is sent at most 3 times and the counter only grows, so no loop.
//   late 401 (token changed since send) -> resend without refresh, attempt + 1
//   401 with the current token           -> refresh, then resend with attempt 2 (today's single refresh + retry)
//   401 at attempt 2                      -> logout (retry_401)
const apiFetch = async <T>(
  endpoint: string,
  options: RequestInit = {},
  attempt: number = 0
): Promise<T> => {
  // Don't make API calls if no token (prevents spam after logout)
  if (!hasValidToken() && !endpoint.includes("/auth/")) {
    throw new Error("Unauthorized");
  }

  // 🔄 MULTI-STORE: Inject store_id into endpoint automatically
  const injectedEndpoint = injectStoreId(endpoint);

  const url = injectedEndpoint.startsWith("http") ? injectedEndpoint : `${API_BASE}${injectedEndpoint}`;

  const isFormData = options.body instanceof FormData;
  const authHeaders = getAuthHeaders() as Record<string, string>;
  const sentToken = authHeaders["Authorization"]?.replace(/^Bearer /, "") || null;
  const response = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders,
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    // 403 — Subscription expired/inactive
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.error_code === "SUBSCRIPTION_INACTIVE") {
        if (!window.location.pathname.startsWith("/subscription") && !window.location.pathname.startsWith("/setup")) {
          sessionStorage.setItem("subscription_expired", "true");
          window.location.href = "/subscription?expired=true";
        }
        throw new Error(errorData.detail || "Subscription expired");
      }
      // Other 403 errors — fall through to generic handler
      throw new Error(errorData.detail || errorData.message || "Forbidden");
    }

    // For login/register endpoints, don't try token refresh - just show the error message
    if (response.status === 401 && endpoint.includes("/auth/login")) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || "Invalid credentials");
    }

    if (response.status === 401 && !endpoint.includes("/auth/refresh") && !endpoint.includes("/auth/login")) {
      // eBay-401 is about the eBay connection, not our session: no refresh, no logout, error goes to the caller
      const errorBody = await response.clone().json().catch(() => ({}));
      if (isEbay401(errorBody)) {
        throw buildApiError(parseApiErrorBody(errorBody, response.status));
      }
    }

    if (response.status === 401 && attempt < 2 && !endpoint.includes("/auth/refresh") && !endpoint.includes("/auth/login")) {
      // Late 401: sent with an old token that another path has replaced meanwhile -> resend, no refresh
      const currentToken = getStoredAccessToken();
      if (sentToken && currentToken && currentToken !== sentToken) {
        return apiFetch<T>(endpoint, options, attempt + 1);
      }

      // Token expired - refresh; concurrent 401s share one request (single flight in refreshAccessToken)
      const newToken = await refreshAccessToken();
      if (newToken) {
        // Retry the original request with new token
        return apiFetch<T>(endpoint, options, 2);
      }
      // Refresh failed: a rejected/missing refresh token logs out; a transient failure keeps the session
      // (the error goes to the caller, the next request tries the refresh again)
      const reason = getLastRefreshFailure();
      if (isTransientRefreshFailure(reason)) {
        console.warn(`[Auth] Session kept after transient refresh failure (${reason}); request not sent`);
        throw new Error("Session refresh temporarily unavailable, please retry");
      }
      handleLogout(reason);
      throw new Error("Unauthorized");
    }

    if (response.status === 401) {
      handleLogout("retry_401");
      throw new Error("Unauthorized");
    }

    const errorData = await response.json().catch(() => ({}));
    // PAKET 1 (18 Aug 2026): the inline reader is replaced by the SINGLE shared
    // reader in utils/apiError.ts. Behaviour for the message itself is unchanged
    // (detail -> message -> error -> `HTTP <status>`); what is NEW is that the
    // machine-readable fields (code / ebay_error_code / details) are attached to
    // the thrown Error instead of being dropped.
    throw buildApiError(parseApiErrorBody(errorData, response.status));
  }

  return response.json();
};

// API Methods
export const api = {
  get: <T>(endpoint: string) => apiFetch<T>(endpoint, { method: "GET" }),

  post: <T>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: "POST",
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
    }),

  put: <T>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: "PUT",
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
    }),

  patch: <T>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: "PATCH",
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
    }),

  delete: <T>(endpoint: string) => apiFetch<T>(endpoint, { method: "DELETE" }),
};

// ============ Dashboard APIs ============
export const dashboardApi = {
  // Get dashboard statistics with period filter and optional store filter
  getStats: (period: string = "1M", storeId?: number) => {
    const params = new URLSearchParams();
    params.append("period", period);
    if (storeId) params.append("store_id", storeId.toString());
    return api.get<any>(`/api/dashboard/stats?${params.toString()}`);
  },

  // Get cached stats for faster loading
  getCachedStats: (period: string = "1M", storeId?: number) => {
    const params = new URLSearchParams();
    params.append("period", period);
    if (storeId) params.append("store_id", storeId.toString());
    return api.get<any>(`/api/dashboard/cached-stats?${params.toString()}`);
  },

  // Get sync status
  getSyncStatus: () => api.get<any>("/api/sync/status"),

  // Trigger manual sync
  triggerSync: () => api.post<any>("/api/sync/trigger"),

  // Get listing counts
  getListingCounts: (storeId?: number) => {
    const params = new URLSearchParams();
    if (storeId) params.append("store_id", storeId.toString());
    return api.get<any>(`/api/listings/counts?${params.toString()}`);
  },

  // Get activity summary for Store Health card
  getActivitySummary: (storeId?: number) => {
    const params = new URLSearchParams();
    if (storeId) params.append("store_id", storeId.toString());
    return api.get<any>(`/api/dashboard/activity-summary?${params.toString()}`);
  },
};

// ============ Authentication APIs ============
export const authApi = {
  // Login
  login: (credentials: { email: string; password: string }) =>
    api.post<any>("/api/auth/login", credentials),

  // Register
  register: (userData: any) => api.post<any>("/api/auth/register", userData),

  // Logout
  logout: () => {
    localStorage.removeItem("authUser");
    return api.post<any>("/api/auth/logout");
  },

  // Refresh token with explicit refresh_token parameter
  refreshToken: (refreshToken: string) =>
    api.post<any>('/api/auth/refresh', { refresh_token: refreshToken }),

  // Complete setup wizard
  completeSetup: () => api.post<any>("/api/auth/complete-setup"),

  // Forgot password
  forgotPassword: (email: string) =>
    api.post<any>('/api/auth/forgot-password', { email }),

  // Verify reset token
  verifyResetToken: (token: string) =>
    api.get<any>(`/api/auth/verify-reset-token?token=${token}`),

  // Reset password
  resetPassword: (token: string, password: string) =>
    api.post<any>('/api/auth/reset-password', { token, new_password: password }),

  // Google login
  googleLogin: () =>
    api.get<any>('/api/auth/google'),

  // Google callback
  googleCallback: (code: string) =>
    api.get<any>(`/api/auth/google/callback?code=${encodeURIComponent(code)}`),

  // Get current user
  me: () =>
    api.get<any>('/api/auth/me'),
};

// ============ Profile APIs ============
export const profileApi = {
  // Get user profile
  getProfile: () => api.get<any>("/api/profile"),

  // Update profile
  updateProfile: (data: any) => api.put<any>("/api/profile", data),

  // Upload avatar
  uploadAvatar: (formData: FormData) =>
    api.post<any>("/api/profile/avatar", formData),

  // Upload cover image
  uploadCover: (formData: FormData) =>
    api.post<any>("/api/profile/cover", formData),

  // Change password
  changePassword: (data: any) =>
    api.post<any>("/api/profile/change-password", data),
};

// ============ Products APIs ============
export const productsApi = {
  // Get products with filters
  getProducts: (params?: {
    limit?: number;
    offset?: number;
    search?: string;
    status?: string;
    source?: string;
    store_id?: number;
    is_managed?: boolean;
    sort_by?: string;
    sort_order?: string;
    oos_reason?: string;
    repricing_error?: boolean;
    error_category?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset !== undefined) query.append("offset", params.offset.toString());
    if (params?.search) query.append("search", params.search);
    if (params?.status) query.append("status", params.status);
    if (params?.source) query.append("source", params.source);
    if (params?.store_id) query.append("store_id", params.store_id.toString());
    if (params?.is_managed !== undefined) query.append("is_managed", params.is_managed.toString());
    if (params?.sort_by) query.append("sort_by", params.sort_by);
    if (params?.sort_order) query.append("sort_order", params.sort_order);
    if (params?.oos_reason) query.append("oos_reason", params.oos_reason);
    if (params?.repricing_error) query.append("repricing_error", "true");
    if (params?.error_category) query.append("error_category", params.error_category);
    return api.get<any>(`/api/products?${query.toString()}`);
  },

  // Get single product
  getProduct: (id: string) => api.get<any>(`/api/products/${id}`),

  // Create product
  createProduct: (data: any) => api.post<any>("/api/products", data),

  // Update product
  updateProduct: (id: string, data: any) =>
    api.put<any>(`/api/products/${id}`, data),

  // Delete product by ID (with store_id for multi-store support)
  deleteProduct: (id: string, storeId?: number) =>
    api.delete<any>(`/api/products/${id}${storeId ? `?store_id=${storeId}` : ''}`),

  // Delete listing by item_id (eBay item ID) (with store_id for multi-store support)
  deleteListing: (itemId: string, storeId?: number) =>
    api.delete<any>(`/api/products/listing/${itemId}${storeId ? `?store_id=${storeId}` : ''}`),

  // Bulk operations
  bulkUpdate: (ids: string[], data: any) =>
    api.post<any>("/api/products/bulk-update", { ids, data }),

  bulkDelete: (productIds: number[]) =>
    api.post<any>("/api/products/bulk-delete", { product_ids: productIds }),

  // Get subscription limits (user-wide, not per-store)
  getLimits: () => api.get<any>("/api/products/limits"),

  // Get variations for a parent listing
  getVariations: (parentItemId: string) =>
    api.get<any>(`/api/products/variations/${parentItemId}`),

  // ===== Product Detail Page APIs =====

  // Get detailed product information
  getProductDetail: (productId: number) =>
    api.get<any>(`/api/products/${productId}/detail`),

  // Update product pricing
  updateProductPricing: (productId: number, data: { ebay_price: number; custom_multiplier?: number }) =>
    api.put<any>(`/api/products/${productId}/pricing`, data),

  // Update product stock
  updateProductStock: (productId: number, data: { quantity: number }) =>
    api.put<any>(`/api/products/${productId}/stock`, data),

  // Mark product as out of stock
  markProductOOS: (productId: number) =>
    api.post<any>(`/api/products/${productId}/mark-oos`),

  // Revise product on eBay
  reviseProduct: (productId: number, data: { title?: string; description?: string; price?: number; quantity?: number }) =>
    api.post<any>(`/api/products/${productId}/revise`, data),

  // Update per-product listing settings
  updateListingSettings: (productId: number, settings: {
    disable_repricing?: boolean;
    disable_stock_sync?: boolean;
    custom_multiplier?: number | null;
    custom_min_profit?: number | null;
    stock_buffer?: number | null;
    is_paused?: boolean;
    is_discontinued?: boolean;
    price_margin_percent?: number | null;
    extra_price?: number | null;
  }) => api.put<any>(`/api/products/${productId}/settings`, settings),

  // Get price history for chart display
  getPriceHistoryChart: (productId: number, period: string = "30d") =>
    api.get<{
      success: boolean;
      asin: string;
      title: string;
      period: string;
      period_days: number;
      chart_data: Array<{
        date: string;
        price: number;
        change_percent: number;
      }>;
      summary: {
        current_price: number;
        min_price: number;
        max_price: number;
        avg_price: number;
        price_range_pct: number;
        change_count: number;
        trend: "UP" | "DOWN" | "STABLE";
        volatility_score: number;
        volatility_label: "LOW" | "MODERATE" | "HIGH" | "VERY_HIGH";
      } | null;
    }>(`/api/products/${productId}/price-history-chart?period=${period}`),

  // Get stock history for chart display
  getStockHistoryChart: (productId: number, period: string = "30d") =>
    api.get<{
      success: boolean;
      asin: string;
      title: string;
      period: string;
      period_days: number;
      chart_data: {
        dates: string[];
        quantities: number[];
        supplier_quantities: number[];
        current_qty: number;
        min_qty: number;
        max_qty: number;
        avg_qty: number;
        changes_count: number;
        volatility_label: "Stable" | "Low" | "Medium" | "High";
        in_stock: boolean;
      };
    }>(`/api/products/${productId}/stock-history-chart?period=${period}`),

  // Check which ASINs are managed
  checkManaged: (asins: string[]) =>
    api.post<any>('/api/products/check-managed', { asins }),

  // Get HTML description for a product
  getDescription: (asin: string) =>
    api.get<any>(`/api/products/${asin}/description`),

  // Save published product
  savePublished: (data: any) =>
    api.post<any>('/api/products/save-published', data),

  // Update supplier ID for a product
  updateSupplierId: (itemId: string, supplierId: string, market: string) =>
    api.post<any>('/api/products/update-supplier-id', {
      item_id: itemId,
      supplier_id: supplierId,
      market
    }),

  // Scrape and revise product asynchronously
  scrapeAndReviseAsync: (asin: string, marketplace?: string) =>
    api.post<any>(`/api/scrape-and-revise-async/${asin}${marketplace ? `?marketplace=${marketplace}` : ''}`),

  // Get async job status
  getJobStatus: (jobId: string) =>
    api.get<any>(`/api/job-status/${jobId}`),

  // Delete product by item_id (POST endpoint for backwards compatibility)
  deleteByItemId: (itemId: string) =>
    api.post<any>('/api/products/delete', { item_id: itemId }),

  // Get product preview with optional store and variations
  getPreview: (asin: string, storeId?: number, fetchVariations?: boolean) => {
    const params = new URLSearchParams();
    if (storeId) params.append('store_id', String(storeId));
    if (fetchVariations) params.append('fetch_variations', 'true');
    const queryString = params.toString();
    return api.get<any>(`/api/products/preview/?asin=${asin}${queryString ? '&' + queryString : ''}`);
  },

  // Publish product to eBay with dynamic query params
  publishProduct: (asin: string, queryParams: Record<string, any>, body: any) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(queryParams)) {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    }
    const queryString = params.toString();
    return api.post<any>(`/api/products/publish/${asin}${queryString ? '?' + queryString : ''}`, body);
  },

  // Link ASIN to product
  linkAsinToProduct: (data: { ebay_item_id: string; asin: string }) =>
    api.post<any>('/api/products/asin/link', data),

  // Bulk link ASINs to products
  bulkLinkAsin: (data: { mappings: Array<{ ebay_item_id: string; asin: string }> }) =>
    api.post<any>('/api/products/asin/bulk-link', data),
};

// ============ Orders APIs ============
export const ordersApi = {
  // Get orders
  getOrders: (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    store_id?: number;
    search?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());
    if (params?.status) query.append("status", params.status);
    if (params?.store_id) query.append("store_id", params.store_id.toString());
    if (params?.search) query.append("search", params.search);
    return api.get<any>(`/api/orders?${query.toString()}`);
  },

  // [F2C] SSE akis bileti: Authorization basligiyla alinir, kisa omurlu, tek kullanimlik (oturum token'i adrese konmaz)
  getStreamTicket: () => api.post<{ ticket: string; expires_in: number }>("/api/orders/stream-ticket"),

  // Get single order
  getOrder: (id: string) => api.get<any>(`/api/orders/${id}`),

  // Get orders to ship
  getToShipOrders: () => api.get<any>("/api/orders/to-ship"),

  // Update order status
  updateOrderStatus: (id: string, data: { status: string; note?: string; trackingNumber?: string }) =>
    api.patch<any>(`/api/orders/${id}/status`, data),

  // Process bulk fulfillment
  bulkFulfill: (orders: Array<{ orderId: string; trackingNumber: string }>) =>
    api.post<any>("/api/orders/bulk-fulfill", { orders }),

  // Manual tracking (Amazon Order ID or carrier tracking)
  manualTracking: (orderId: string, params: {
    amazon_order_id?: string;
    tracking_number?: string;
    carrier?: string;
  }) =>
    api.post<any>(`/api/orders/${orderId}/manual-tracking`, params),

  // Link ASIN to order item
  linkAsin: (orderId: string, data: { item_id: string; asin: string; amazon_domain?: string }) =>
    api.post<any>(`/api/orders/${orderId}/link-asin`, data),

  // Start auto order
  startAutoOrder: (orderId: string) =>
    api.post<any>(`/api/orders/${orderId}/ao-start`, {}),

  // Cancel auto order
  cancelAutoOrder: (orderId: string) =>
    api.post<any>(`/api/orders/${orderId}/ao-cancel`, {}),

  // Toggle per-order AO skip
  toggleAoSkip: (orderId: number | string) =>
    api.patch<any>(`/api/orders/${orderId}/ao-skip`, {}),
};

// ============ eBay APIs (for future integration) ============
export const ebayApi = {
  // OAuth & Connection
  getAuthUrl: (marketplaceId: string = "EBAY_GB") => api.get<any>(`/api/ebay/auth/url?marketplace_id=${marketplaceId}`),
  getConnectionStatus: () => api.get<any>("/api/ebay/connection"),
  getProfile: () => api.get<any>("/api/ebay/profile"),
  // [EBAY-BAGLANTI] 19 Eki 2026: hangi magaza kesilecek acikca gonderilir (backend routers/ebay/baglanti.py)
  disconnectAccount: (storeId?: number) =>
    api.post<any>("/api/ebay/disconnect", storeId != null ? { store_id: storeId } : {}),

  // Sync - with optional store_id for multi-store support
  syncProducts: (storeId?: number) => {
    const params = storeId ? `?store_id=${storeId}` : "";
    return api.post<any>(`/api/ebay/sync/products${params}`);
  },
  syncOrders: (storeId?: number) => {
    const params = storeId ? `?store_id=${storeId}` : "";
    return api.post<any>(`/api/ebay/sync/orders${params}`);
  },
  syncPolicies: (storeId?: number) => {
    const params = storeId ? `?store_id=${storeId}` : "";
    return api.post<any>(`/api/ebay/sync/policies${params}`);
  },
  getSyncStatus: () => api.get<any>("/api/sync/status"),

  // Listings
  getListings: (params?: { limit?: number; offset?: number; store_id?: number }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());
    if (params?.store_id) query.append("store_id", params.store_id.toString());
    return api.get<any>(`/ebay/listings?${query.toString()}`);
  },

  // Backfill missing prices for eBay listings
  backfillMissingPrices: () =>
    api.post<any>('/ebay/listings/backfill-missing-prices'),

  // Save draft listings to eBay
  saveDraftListings: (data: any, storeId?: number) => {
    const params = storeId ? `&store_id=${storeId}` : '';
    return api.post<any>(`/ebay/listings/save?user_id=${data.user_id || ''}${params}`, data);
  },

  // Get category aspects for eBay category
  getCategoryAspects: (categoryId: string | number) =>
    api.get<any>(`/ebay/category_aspects?category_id=${categoryId}`),
};

// ============ Drafts APIs ============
export const draftsApi = {
  // Get drafts with optional store filter
  getDrafts: (storeId?: number) => {
    const params = new URLSearchParams();
    if (storeId) params.append("store_id", storeId.toString());
    return api.get<any>(`/api/drafts?${params.toString()}`);
  },

  // Get single draft by ASIN
  getDraft: (asin: string) => api.get<any>(`/api/drafts/get/${asin}`),

  // Save draft
  saveDraft: (data: { asin: string; data: any }) =>
    api.post<any>("/api/drafts/save", data),

  // Bulk save scraped drafts (N draft tek transaction'da upsert)
  saveDraftsBulk: (data: { drafts: Array<{ asin: string; data: any }> }) =>
    api.post<any>("/api/drafts/save-bulk", data),

  // Add multiple drafts
  addDrafts: (drafts: Array<{ asin: string; supplier?: string; region?: string }>) =>
    api.post<any>("/api/drafts/add", { drafts }),

  // Delete draft by ASIN
  deleteDraft: (asin: string) => api.delete<any>(`/api/drafts/delete/${asin}`),

  // Delete multiple drafts
  deleteMany: (asins: string[]) => api.post<any>("/api/drafts/delete-many", asins),

  // Clear all drafts
  clearAll: () => api.delete<any>("/api/drafts/clear"),

  // Get variations for a draft
  getVariations: (asin: string) =>
    api.get<any>(`/api/drafts/variations/${asin}`),
};

// ============ Kepa API (Dropshipping Source) ============
export const kepaApi = {
  // Search products from Kepa
  searchProducts: (query: string, filters?: any) =>
    api.post<any>("/api/kepa/search", { query, filters }),

  // Get product details from Kepa
  getProductDetails: (productId: string) =>
    api.get<any>(`/api/kepa/products/${productId}`),

  // Import product from Kepa to store
  importProduct: (productId: string, customization?: any) =>
    api.post<any>("/api/kepa/import", { productId, customization }),

  // Get supplier info
  getSupplierInfo: (supplierId: string) =>
    api.get<any>(`/api/kepa/suppliers/${supplierId}`),

  // Track shipment
  trackShipment: (trackingNumber: string) =>
    api.get<any>(`/api/kepa/track/${trackingNumber}`),
};

// ============ Settings APIs ============
export const settingsApi = {
  // General settings (legacy - user-level)
  getSettings: () => api.get<any>("/api/settings"),
  updateSettings: (data: any) => api.put<any>("/api/settings", data),

  // Store-specific settings (multi-store support)
  getStoreSettings: (storeId?: number) => {
    const query = storeId ? `?store_id=${storeId}` : "";
    return api.get<any>(`/api/settings/store${query}`);
  },
  updateStoreSettings: (data: any, storeId?: number) => {
    const query = storeId ? `?store_id=${storeId}` : "";
    return api.put<any>(`/api/settings/store${query}`, data);
  },
  getStoreSettingsById: (storeId: number) =>
    api.get<any>(`/api/settings/store/${storeId}`),
  updateStoreSettingsById: (storeId: number, data: any) =>
    api.put<any>(`/api/settings/store/${storeId}`, data),

  // Pricing rules
  getPricingRules: () => api.get<any>("/api/settings/pricing"),
  updatePricingRules: (data: any) =>
    api.put<any>("/api/settings/pricing", data),

  // Notification settings
  getNotificationSettings: () => api.get<any>("/api/settings/notifications"),
  updateNotificationSettings: (data: any) =>
    api.put<any>("/api/settings/notifications", data),

  // eBay policies
  syncEbayPolicies: (storeId: number) =>
    api.post<any>(`/api/ebay/sync/policies?store_id=${storeId}`),
  getBusinessPolicies: (type: 'payment' | 'shipping' | 'return', storeId?: number) => {
    const query = storeId ? `?store_id=${storeId}` : '';
    return api.get<any>(`/api/settings/business-policies/${type}${query}`);
  },

  // Templates
  getTemplateHtml: (themeId: string) =>
    api.get<any>(`/api/templates/${themeId}/template.html`),

  // VERO list sharing
  shareVeroList: (data: any) =>
    api.post<any>('/api/settings/share-vero', data),

  // User settings
  getUserSettings: () =>
    api.get<any>('/api/user-settings'),

  saveUserSettings: (data: any) =>
    api.post<any>('/api/user-settings', data),

  // Auto Order (AO) settings
  getAOSettings: () =>
    api.get<any>('/api/settings/ao-settings'),

  updateAOSettings: (changes: any) =>
    api.patch<any>('/api/settings/ao-settings', changes),
};

// ============ Admin APIs (Multi-tenant) ============
export const adminApi = {
  // User management
  getUsers: () => api.get<any>("/api/admin/users"),
  getUser: (userId: string) => api.get<any>(`/api/admin/users/${userId}`),
  createUser: (data: any) => api.post<any>("/api/admin/users", data),
  updateUser: (userId: string, data: any) =>
    api.put<any>(`/api/admin/users/${userId}`, data),
  deleteUser: (userId: string) =>
    api.delete<any>(`/api/admin/users/${userId}`),

  // User listings
  getUserListings: (userId: string, params?: {
    page?: number;
    per_page?: number;
    status?: string;
    search?: string;
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append("page", params.page.toString());
    if (params?.per_page) query.append("per_page", params.per_page.toString());
    if (params?.status) query.append("status", params.status);
    if (params?.search) query.append("search", params.search);
    return api.get<any>(`/api/admin/users/${userId}/listings?${query.toString()}`);
  },

  // System stats
  getSystemStats: () => api.get<any>("/api/admin/stats"),

  // Logs
  getLogs: (params?: { limit?: number; level?: string }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.level) query.append("level", params.level);
    return api.get<any>(`/api/admin/logs?${query.toString()}`);
  },

  // Subscription health
  getSubscriptionStatuses: () => api.get<any>("/api/admin/subscription-status"),
  syncProviderStatuses: () => api.post<any>("/api/admin/sync-provider-status", {}),

  // Ops health
  getOpsHealth: () => api.get<any>("/api/admin/system/ops-health"),
  fixMismatches: () => api.post<any>("/api/admin/system/ops/fix-mismatches", {}),
  retryError: (productId: number) => api.post<any>(`/api/admin/system/ops/retry-error/${productId}`, {}),
  dismissError: (productId: number) => api.post<any>(`/api/admin/system/ops/dismiss-error/${productId}`, {}),
  triggerPush: (userId: string) => api.post<any>(`/api/admin/system/ops/trigger-push/${userId}`, {}),

  // Manual subscription (no PayPal/Stripe)
  manualSubscription: (userId: string, data: {
    plan: string;
    billing_cycle: string;
    duration_months?: number;
    reason: string;
  }) => api.post<any>(`/api/admin/users/${userId}/manual-subscription`, data),

  // User Activation / Suspension
  activateUser: (userId: string) => api.post<any>(`/api/admin/users/${userId}/activate`, {}),
  suspendUser: (userId: string) => api.post<any>(`/api/admin/users/${userId}/suspend`, {}),

  // Impersonate user
  impersonate: (userId: string) => api.post<any>(`/api/admin/impersonate/${userId}`, {}),

  // Admin Email
  sendEmail: (data: {
    user_id?: number;
    to_email?: string;
    subject: string;
    heading: string;
    message: string;
    button_text?: string;
    button_url?: string;
  }) => api.post<any>("/api/admin/emails/send", data),

  bulkSendEmail: (data: {
    target: "all" | "paid" | "trial" | "expired";
    subject: string;
    heading: string;
    message: string;
    button_text?: string;
    button_url?: string;
  }) => api.post<any>("/api/admin/emails/bulk-send", data),

  previewEmail: (data: {
    subject: string;
    heading: string;
    message: string;
    button_text?: string;
    button_url?: string;
  }) => api.post<any>("/api/admin/emails/preview", data),

  // User Activity Timeline
  getUserActivity: (userId: string, eventType?: string) => {
    const query = eventType && eventType !== "all" ? `?event_type=${eventType}` : "";
    return api.get<any>(`/api/admin/users/${userId}/activity${query}`);
  },

  // Churn Dashboard
  getChurnDashboard: () => api.get<any>("/api/admin/churn-dashboard"),

  // Extend Trial
  extendTrial: (userId: string, days: number = 7) =>
    api.post<any>(`/api/admin/users/${userId}/extend-trial`, { days }),

  // Discount Codes
  getDiscountCodes: (isActive?: boolean) => {
    const params = isActive !== undefined ? `?is_active=${isActive}` : "";
    return api.get<any>(`/api/admin/discount-codes${params}`);
  },
  createDiscountCode: (data: any) => api.post<any>("/api/admin/discount-codes", data),
  updateDiscountCode: (id: number, data: any) => api.put<any>(`/api/admin/discount-codes/${id}`, data),
  deleteDiscountCode: (id: number) => api.delete<any>(`/api/admin/discount-codes/${id}`),
  getDiscountCodeStats: (id: number) => api.get<any>(`/api/admin/discount-codes/${id}/stats`),

  // Audit & Security
  getAuditLogs: (params?: { limit?: number }) =>
    api.get<any>(`/api/admin/audit-logs?limit=${params?.limit || 100}`),
  getFailedLogins: (limit = 50) =>
    api.get<any>(`/api/admin/security/failed-logins?limit=${limit}`),
  blockIp: (ip: string, reason?: string) =>
    api.post<any>('/api/admin/security/block-ip', { ip, reason }),

  // Credits
  getCreditTransactions: (limit = 100) =>
    api.get<any>(`/api/admin/credits/transactions?limit=${limit}`),
  addCredits: (userId: string, amount: number, reason: string) =>
    api.post<any>(`/api/admin/users/${encodeURIComponent(userId)}/credits/add`, { amount, reason }),
  deductCredits: (userId: string, amount: number, reason: string) =>
    api.post<any>(`/api/admin/users/${encodeURIComponent(userId)}/credits/deduct`, { amount, reason }),

  // Trial History
  getTrialHistory: (params?: { user_id?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.user_id) query.append('user_id', params.user_id);
    if (params?.limit) query.append('limit', params.limit.toString());
    return api.get<any>(`/api/admin/trial-history?${query.toString()}`);
  },
  createTrialHistory: (data: any) =>
    api.post<any>('/api/admin/trial-history', data),
  deleteTrialHistory: (id: string) =>
    api.delete<any>(`/api/admin/trial-history/${id}`),

  // eBay Accounts
  getEbayAccounts: (params?: { user_id?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.user_id) query.append('user_id', params.user_id);
    if (params?.limit) query.append('limit', params.limit.toString());
    return api.get<any>(`/api/admin/ebay-accounts?${query.toString()}`);
  },
  // [EBAY-BAGLANTI] 19 Eki 2026: backend bu iki ucu PUT ile tanimliyor (admin.py) — POST 405 donuyordu
  disconnectEbayAccount: (accountId: string) =>
    api.put<any>(`/api/admin/ebay-accounts/${accountId}/disconnect`, {}),
  reconnectEbayAccount: (accountId: string) =>
    api.put<any>(`/api/admin/ebay-accounts/${accountId}/reconnect`, {}),

  // Invoices
  getAdminInvoices: (limit = 100) =>
    api.get<any>(`/api/admin/invoices?limit=${limit}`),
  generateInvoice: (data: any) =>
    api.post<any>('/api/admin/invoices/generate', data),
  updateAdminInvoiceStatus: (invoiceId: string, status: string) =>
    api.put<any>(`/api/admin/invoices/${invoiceId}/status?status=${status}`),

  // Payments
  getPaymentHistory: (limit = 100) =>
    api.get<any>(`/api/admin/payments/history?limit=${limit}`),
  getPaymentStats: () =>
    api.get<any>('/api/admin/payments/stats'),
  processPayment: (data: any) =>
    api.post<any>('/api/admin/payments/process', data),
  refundPayment: (paymentId: string, data: any) =>
    api.post<any>(`/api/admin/payments/${paymentId}/refund`, data),
  // [ODEME-DUZELTME] saglayicinin gercek tahsilati (tutar + para birimi, iade edilebilir) — iade penceresinin tek kaynagi
  refundPreview: (paymentId: string) =>
    api.get<any>(`/api/admin/payments/${paymentId}/refund-preview`),

  // Stores
  getAdminStores: (limit = 500) =>
    api.get<any>(`/api/admin/stores?limit=${limit}`),
  syncStore: (userId: string) =>
    api.post<any>(`/api/admin/stores/${userId}/sync`, {}),

  // Settings
  getAdminSettings: () =>
    api.get<any>('/api/admin/settings'),
  updateAdminSettings: (data: any) =>
    api.put<any>('/api/admin/settings', data),
  getSubscriptionPlans: () =>
    api.get<any>('/api/admin/settings/subscription-plans'),
  updateSubscriptionPlans: (data: any) =>
    api.put<any>('/api/admin/settings/subscription-plans', data),

  // Maintenance
  getMaintenanceMode: () =>
    api.get<any>('/api/admin/maintenance'),
  setMaintenanceMode: (enabled: boolean, message?: string) =>
    api.post<any>('/api/admin/maintenance', { enabled, message }),

  // System Health
  getSystemHealth: () =>
    api.get<any>('/api/admin/system/health'),
  getSystemMetrics: (period: string) =>
    api.get<any>(`/api/admin/system/metrics?period=${period}`),
  getDatabaseInfo: () =>
    api.get<any>('/api/admin/system/database-info'),
  getRecentErrors: (limit = 50) =>
    api.get<any>(`/api/admin/system/recent-errors?limit=${limit}`),
  getSchedulerStatus: () =>
    api.get<any>('/api/admin/system/scheduler-status'),
  getApplicationLogs: (limit = 200) =>
    api.get<any>(`/api/admin/system/application-logs?limit=${limit}`),
  triggerSync: (syncType: string) =>
    api.post<any>(`/api/admin/system/trigger-sync?sync_type=${syncType}`, {}),

  // Ops Actions (ek)
  repushMismatch: (data: any) =>
    api.post<any>('/api/admin/system/ops/repush-mismatch', data),
  dismissMismatch: (data: any) =>
    api.post<any>('/api/admin/system/ops/dismiss-mismatch', data),

  // Reports & Monitoring
  getRevenueReport: (period: string) =>
    api.get<any>(`/api/admin/reports/revenue?period=${period}`),
  getScraperHealth: (hours = 24) =>
    api.get<any>(`/api/admin/scraper-health?hours=${hours}`),
  getExpiringTrials: (days = 7) =>
    api.get<any>(`/api/admin/expiring-trials?days=${days}`),
  getAnnouncements: (limit = 5) =>
    api.get<any>(`/api/admin/announcements?limit=${limit}`),
};

// ============ Billing & Subscription APIs ============
export const billingApi = {
  // Get current subscription info
  getSubscription: () => api.get<any>("/api/billing"),

  // Get provider subscription status (PayPal/Stripe real status)
  getProviderStatus: () => api.get<any>("/api/billing/provider-status"),

  // Get available plans
  getPlans: () => api.get<any>("/api/billing/plans"),

  // Upgrade subscription
  upgrade: (data: {
    plan: string;
    billing_cycle?: string;
    enterprise_products?: number;
  }) => api.post<any>("/api/billing/upgrade", data),

  // Add extra product blocks
  addProducts: (blocks: number) =>
    api.post<any>("/api/billing/add-products", { blocks }),

  // Create checkout session
  createCheckout: (data: {
    plan?: string;
    billing_cycle?: string;
    enterprise_products?: number;
    extra_product_blocks?: number;
    payment_method?: string;
    discount_code?: string;
  }) => api.post<any>("/api/billing/create-checkout", data),

  // Validate discount code
  validateDiscount: (data: { code: string; plan: string; billing_cycle: string }) =>
    api.post<any>("/api/billing/validate-discount", data),

  // Sync checkout session (for local testing without webhooks)
  syncCheckout: (sessionId: string) =>
    api.post<any>(`/api/billing/sync-checkout/${sessionId}`, {}),

  // Start trial
  startTrial: (data: { plan: string; billing_cycle?: string; enterprise_products?: number }) =>
    api.post<any>("/api/billing/start-trial", data),

  // Cancel subscription
  cancel: (reason?: string) =>
    api.post<any>("/api/billing/cancel", { reason }),

  // Get invoices
  getInvoices: (params?: { limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());
    return api.get<any>(`/api/billing/invoices?${query.toString()}`);
  },

  // Get invoice details
  getInvoice: (invoiceId: number) => api.get<any>(`/api/billing/invoices/${invoiceId}`),

  // Download invoice PDF
  downloadInvoicePdf: async (invoiceId: number, invoiceNumber: string) => {
    const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
    const authUser = localStorage.getItem("authUser");
    let token = "";
    if (authUser) {
      try {
        const parsed = JSON.parse(authUser);
        token = parsed?.access_token || parsed?.token || "";
      } catch (e) {
        console.error("Error parsing authUser:", e);
      }
    }

    const response = await fetch(`${API_BASE}/api/billing/invoices/${invoiceId}/download`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download invoice: ${response.status}`);
    }

    const blob = await response.blob();

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Invoice-${invoiceNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  },

  // Get invoice HTML content (for preview in modal)
  getInvoiceHtml: async (invoiceId: number): Promise<string> => {
    const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
    const authUser = localStorage.getItem("authUser");
    let token = "";
    if (authUser) {
      try {
        const parsed = JSON.parse(authUser);
        token = parsed?.access_token || parsed?.token || "";
      } catch (e) {
        console.error("Error parsing authUser:", e);
      }
    }

    const response = await fetch(`${API_BASE}/api/billing/invoices/${invoiceId}/html`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get invoice HTML: ${response.status}`);
    }

    return response.text();
  },

  // Get payment methods
  getPaymentMethods: () => api.get<any>("/api/billing/payment-methods"),

  // Credits — transaction history + balance
  getCreditHistory: (limit = 50, offset = 0) =>
    api.get<any>(`/api/billing/credit-history?limit=${limit}&offset=${offset}`),

  // Credits — available packs for purchase
  getCreditPacks: () => api.get<any>("/api/billing/credit-packs"),

  // Credits — buy credit pack (Stripe checkout redirect)
  buyCredits: (data: { pack_id: string; payment_method: string }) =>
    api.post<any>("/api/billing/buy-credits", data),
};

// ============ Tickets/Support APIs ============
export const ticketsApi = {
  // Get all tickets
  getTickets: (params?: { status?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.limit) query.append('limit', params.limit.toString());
    return api.get<any>(`/api/tickets?${query.toString()}`);
  },

  // Get single ticket
  getTicket: (ticketId: string) => api.get<any>(`/api/tickets/${ticketId}`),

  // Create ticket
  createTicket: (data: {
    subject?: string;
    message?: string;
    priority?: string;
    store_id?: number;
    item_id?: string;
    item_title?: string;
    // Additional fields for product reports
    report_type?: string;
    description?: string;
    item_data?: any;
    user_id?: string;
  }) => api.post<any>("/api/tickets", data),

  // Update ticket
  updateTicket: (ticketId: string, data: { status?: string; priority?: string }) =>
    api.put<any>(`/api/tickets/${ticketId}`, data),

  // Add message to ticket
  addMessage: (ticketId: string, message: string) =>
    api.post<any>(`/api/tickets/${ticketId}/messages`, { message }),

  // Get ticket messages
  getMessages: (ticketId: string) => api.get<any>(`/api/tickets/${ticketId}/messages`),
};

// ============================================================================
// Notifications API (User notifications/announcements)
// ============================================================================
export const notificationsApi = {
  // Get notifications for current user
  getNotifications: (params?: { limit?: number; offset?: number; include_dismissed?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.offset) searchParams.append("offset", params.offset.toString());
    if (params?.include_dismissed) searchParams.append("include_dismissed", "true");
    const query = searchParams.toString();
    return api.get<{
      notifications: Array<{
        id: number;
        announcement_id: number;
        title: string;
        message: string;
        type: string;
        link_url?: string;
        link_text?: string;
        is_read: boolean;
        is_dismissed: boolean;
        created_at: string;
        priority: number;
      }>;
      total: number;
      unread_count: number;
    }>(`/api/notifications${query ? `?${query}` : ""}`);
  },

  // Get unread count only
  getUnreadCount: () => api.get<{ count: number }>("/api/notifications/unread-count"),

  // Mark notification as read
  markAsRead: (notificationId: number) =>
    api.post<{ success: boolean }>(`/api/notifications/${notificationId}/read`),

  // Dismiss notification
  dismiss: (notificationId: number) =>
    api.post<{ success: boolean }>(`/api/notifications/${notificationId}/dismiss`),

  // Mark all as read
  markAllRead: () => api.post<{ success: boolean; message: string }>("/api/notifications/mark-all-read"),
};

// ============================================================================
// Admin Announcements API
// ============================================================================
export const announcementsApi = {
  // Get all announcements (admin)
  getAnnouncements: (params?: { limit?: number; offset?: number; include_inactive?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.offset) searchParams.append("offset", params.offset.toString());
    if (params?.include_inactive) searchParams.append("include_inactive", "true");
    const query = searchParams.toString();
    return api.get<{
      success: boolean;
      data: Array<{
        id: number;
        title: string;
        message: string;
        type: string;
        target: string;
        target_users?: string[];
        is_active: boolean;
        priority: number;
        link_url?: string;
        link_text?: string;
        created_by?: string;
        created_at?: string;
        expires_at?: string;
        stats: { total_sent: number; total_read: number; read_rate: number };
      }>;
      total: number;
    }>(`/api/admin/announcements${query ? `?${query}` : ""}`);
  },

  // Create announcement
  createAnnouncement: (data: {
    title: string;
    message: string;
    type?: string;
    target?: string;
    target_users?: string[];
    priority?: number;
    link_url?: string;
    link_text?: string;
    expires_at?: string;
  }) => api.post<{ success: boolean; message: string; data: { id: number; title: string } }>("/api/admin/announcements", data),

  // Update announcement
  updateAnnouncement: (id: number, data: {
    title?: string;
    message?: string;
    type?: string;
    target?: string;
    target_users?: string[];
    priority?: number;
    link_url?: string;
    link_text?: string;
    is_active?: boolean;
    expires_at?: string;
  }) => api.put<{ success: boolean; message: string }>(`/api/admin/announcements/${id}`, data),

  // Delete announcement
  deleteAnnouncement: (id: number) =>
    api.delete<{ success: boolean; message: string }>(`/api/admin/announcements/${id}`),

  // Get announcement stats
  getAnnouncementStats: (id: number) =>
    api.get<{
      success: boolean;
      data: {
        announcement_id: number;
        title: string;
        total_sent: number;
        total_read: number;
        total_dismissed: number;
        read_rate: number;
        dismiss_rate: number;
      };
    }>(`/api/admin/announcements/${id}/stats`),
};

// ============ Mailbox / Email API ============
export const emailApi = {
  getEmails: (folder: string) => api.get<any>(`/api/email/${folder}`),
  getEmailById: (emailId: number | string) => api.get<any>(`/api/email/${emailId}`),
  updateEmail: (emailId: number | string, data: any) => api.put<any>(`/api/email/${emailId}`, data),
  trashEmail: (emailId: number | string) => api.delete<any>(`/api/email/${emailId}`),
  deleteEmail: (emailId: number | string) => api.delete<any>(`/api/email/${emailId}?permanent=true`),
  sendEmail: (mailData: any) => api.post<any>("/api/email/send", mailData),
  replyEmail: (emailId: number | string, data: any) => api.post<any>(`/api/email/${emailId}/reply`, data),
  getStats: () => api.get<any>("/api/email/stats"),

  // Gmail integration (OAuth — dormant, IMAP'e gecildi; rollback icin korunuyor)
  getGmailStatus: () => api.get<any>('/api/gmail/status'),
  authorizeGmail: () => api.get<any>('/api/gmail/authorize'),
  disconnectGmail: () => api.delete<any>('/api/gmail/disconnect'),

  // IMAP Email Integration (App Password — Gmail OAuth alternatifi)
  getEmailIntegrationStatus: () => api.get<any>('/api/email-integration/status'),
  connectEmailIntegration: (email: string, appPassword: string) =>
    api.post<any>('/api/email-integration/connect', { email, app_password: appPassword }),
  disconnectEmailIntegration: () => api.delete<any>('/api/email-integration/disconnect'),
  testEmailIntegration: () => api.post<any>('/api/email-integration/test', {}),

  // Email settings
  getEmailConfig: () =>
    api.get<any>('/api/email/settings/config'),
  saveEmailConfig: (data: any) =>
    api.post<any>('/api/email/settings/config', data),
  testEmailConnection: (type: 'imap' | 'smtp', testEmail?: string) =>
    api.post<any>('/api/email/settings/test', { connection_type: type, test_email: testEmail }),
  syncEmail: () =>
    api.post<any>('/api/email/sync', {}),
};

// ============ Preview with Polling (Async Preview Support) ============

/**
 * Start a preview job and poll for the result.
 * Backend may return:
 *   - 202 + { job_id, status: "queued" }  (async mode)
 *   - 200 + { success: true, data: {...} } (sync mode / feature flag off)
 *
 * This helper handles both cases transparently.
 */
export async function previewWithPolling(
  endpoint: string,
  options?: {
    pollInterval?: number;    // default 2000ms
    maxTimeout?: number;      // default 300000ms (5 min)
    onStatusChange?: (status: string) => void;
    signal?: AbortSignal;     // for cancellation
  }
): Promise<any> {
  const { pollInterval = 2000, maxTimeout = 300000, onStatusChange, signal } = options || {};

  // 1. Start the preview request
  // Use raw fetch via apiFetch wrapper — need to handle 202 specially
  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const injectedEndpoint = injectStoreId(endpoint);
  const url = injectedEndpoint.startsWith("http") ? injectedEndpoint : `${API_BASE_URL}${injectedEndpoint}`;

  const startResponse = await fetch(url, {
    method: "GET",
    headers: {
      ...getAuthHeaders(),
    },
    signal,
  });

  const responseData = await startResponse.json();

  // Sync mode: backend returned full result directly (feature flag off)
  if (startResponse.status === 200 && (responseData.success || responseData.data)) {
    return responseData;
  }

  // Auth errors
  if (startResponse.status === 401) {
    throw new Error("Unauthorized");
  }
  if (startResponse.status === 403) {
    throw new Error(responseData.detail || "Forbidden");
  }
  if (startResponse.status >= 400 && startResponse.status !== 202) {
    throw new Error(responseData.detail || responseData.message || `HTTP ${startResponse.status}`);
  }

  // Async mode: 202 + job_id
  const jobId = responseData.job_id;
  if (!jobId) {
    throw new Error("No job_id returned from async preview");
  }

  onStatusChange?.("queued");

  // 2. Poll for result
  const startTime = Date.now();
  while (Date.now() - startTime < maxTimeout) {
    if (signal?.aborted) {
      throw new Error("Preview cancelled");
    }

    await new Promise(r => setTimeout(r, pollInterval));

    try {
      const statusResult = await api.get<any>(`/api/jobs/${jobId}/status`);

      if (statusResult.status === "finished") {
        onStatusChange?.("completed");
        return statusResult.data; // Same format as sync response
      }

      if (statusResult.status === "failed") {
        const errorMsg = statusResult.error || "Preview failed";
        throw new Error(typeof errorMsg === "string" ? errorMsg.substring(0, 200) : "Preview failed");
      }

      // Still running — "queued" or "started"
      onStatusChange?.(statusResult.status);
    } catch (pollErr: any) {
      // 404 means job expired or not found
      if (pollErr?.message?.includes("404") || pollErr?.message?.includes("not found")) {
        throw new Error("Preview job not found");
      }
      // Re-throw other errors
      throw pollErr;
    }
  }

  throw new Error("Preview timed out (5 min)");
}

// ============ AI APIs ============
export const aiApi = {
  // Optimize product title with AI
  optimizeTitle: (data: {
    title: string;
    asin?: string;
    marketplace?: string;
    category?: string;
    brand?: string;
    features?: string;
  }) => api.post<any>('/api/ai/optimize-title', data),
};

// ============ Apps/Chat APIs ============
export const chatApi = {
  getRooms: () =>
    api.get<any>('/api/apps/chat/rooms'),
  getRoomMessages: (roomId: string | number) =>
    api.get<any>(`/api/apps/chat/rooms/${roomId}/messages`),
  sendMessage: (roomId: string | number, data: any) =>
    api.post<any>(`/api/apps/chat/rooms/${roomId}/messages`, data),
  getChatUsers: () =>
    api.get<any>('/api/apps/chat/users'),
  uploadFile: (roomId: string | number, formData: FormData) =>
    api.post<any>(`/api/apps/chat/rooms/${roomId}/upload`, formData),
};

// ============ Apps/Calendar APIs ============
export const calendarApi = {
  getEvents: () =>
    api.get<any>('/api/apps/calendar/events'),
  createEvent: (data: any) =>
    api.post<any>('/api/apps/calendar/events', data),
  updateEvent: (id: string | number, data: any) =>
    api.put<any>(`/api/apps/calendar/events/${id}`, data),
  deleteEvent: (id: string | number) =>
    api.delete<any>(`/api/apps/calendar/events/${id}`),
};

// ============ Apps/Tickets APIs (Support Tickets) ============
export const appTicketsApi = {
  getTickets: () =>
    api.get<any>('/api/apps/tickets'),
  createTicket: (data: any) =>
    api.post<any>('/api/apps/tickets', data),
  updateTicket: (id: string | number, data: any) =>
    api.put<any>(`/api/apps/tickets/${id}`, data),
  deleteTicket: (id: string | number) =>
    api.delete<any>(`/api/apps/tickets/${id}`),
  getTicket: (id: string) =>
    api.get<any>(`/api/apps/tickets/${id}`),
  getTicketMessages: (id: string) =>
    api.get<any>(`/api/apps/tickets/${id}/messages`),
  sendTicketMessage: (id: string, message: string, attachments?: any[]) =>
    api.post<any>(`/api/apps/tickets/${id}/messages`, { message, attachments }),
  uploadTicketFile: (formData: FormData) =>
    api.post<any>('/api/apps/tickets/upload', formData),
  getUnreadCount: () =>
    api.get<any>('/api/apps/tickets/unread-count'),
  getAdminUnreadCount: () =>
    api.get<any>('/api/apps/tickets/admin-unread-count'),
};

// ============ Apps/Invoices APIs ============
export const appInvoicesApi = {
  getInvoices: () =>
    api.get<any>('/api/apps/invoices'),
  createInvoice: (data: any) =>
    api.post<any>('/api/apps/invoices', data),
  updateInvoiceStatus: (id: string | number, status: string) =>
    api.put<any>(`/api/apps/invoices/${id}/status?status=${status}`),
  cancelInvoice: (id: string | number) =>
    api.put<any>(`/api/apps/invoices/${id}/status?status=cancelled`),
};

export default api;
